import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import type { QuoteDetailDto, UpdateQuoteRequest } from "@/lib/quotes";
import { quotesApi } from "@/lib/quotes";
import { formatDate } from "@/lib/format";
import { QuoteStatusBadge } from "@/components/quotes/quote-status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { EmployeePicker } from "@/components/employee-picker";
import { toast } from "@/hooks/use-toast";

export interface QuoteHeaderProps {
  quote: QuoteDetailDto;
}

interface HeaderField {
  label: string;
  /** Render content. Returning null hides the cell entirely. */
  value: React.ReactNode;
}


/**
 * Quote-page header strip matching `Quote Details.png`. Renders 7 always-on
 * fields plus a conditional "Copied from Quote" cell when the quote was
 * created via Duplicate. Status badge sits inline next to Quote Ref.
 *
 * Always-on fields: Quote Ref, Customer Name, Quote Date, Quoted By,
 * Created By, Last Modified By, Due Date (+ conditional Copied from Quote).
 *
 * Replaces the previous `metaStrip` headerVariant on this page — mounted via
 * the new `headerOverride` slot on the `DetailPage` archetype (Task 13).
 */
export function QuoteHeader({ quote }: QuoteHeaderProps) {
  const [open, setOpen] = useState(false);
  const [ownerId, setOwnerId] = useState<number | null>(quote.ownerEmployeeId);
  const [ownerName, setOwnerName] = useState<string | null>(quote.ownerEmployeeName);
  
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: async () => {
      if (!quote.rowVersionBase64) return;
      const patchBody: UpdateQuoteRequest = {
        ownerEmployeeId: ownerId,
        rowVersionBase64: quote.rowVersionBase64,
      };
      await quotesApi.patch(quote.quoteId, patchBody);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote", quote.quoteId] });
      toast.success("Quote header updated.");
      setOpen(false);
    },
    onError: (err: Error) => {
      toast.error(`Update failed: ${err.message}`);
    },
  });

  const fields: HeaderField[] = [
    {
      label: "Quote Ref",
      value: (
        <span className="inline-flex items-center gap-2">
          <span className="font-bold">{quote.quoteRef}</span>
          <QuoteStatusBadge status={quote.status} />
        </span>
      ),
    },
    { label: "Customer Name", value: quote.clientName ?? "—" },
    { label: "Quote Date", value: quote.createdOn ? formatDate(quote.createdOn) : "—" },
    { label: "Quoted By", value: quote.ownerEmployeeName ?? "—" },
  ];

  if (quote.copiedFromQuoteId != null) {
    fields.push({
      label: "Copied from Quote",
      value: (
        <Link
          to={`/quotes/${quote.copiedFromQuoteId}`}
          className="text-primary underline font-bold"
        >
          {quote.copiedFromQuoteRef ?? `Q#${quote.copiedFromQuoteId}`}
        </Link>
      ),
    });
  }

  fields.push(
    { label: "Created By", value: quote.createdByName ?? "—" },
    { label: "Last Modified By", value: quote.modifiedByName ?? "—" },
    { label: "Due Date", value: quote.validUntil ? formatDate(quote.validUntil) : "—" },
  );

  return (
    <header className="mb-4 relative bg-white rounded-lg px-6 py-4 shadow-sm border border-slate-200 group">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2"
        onClick={() => {
          setOwnerId(quote.ownerEmployeeId);
          setOwnerName(quote.ownerEmployeeName);
          setOpen(true);
        }}
        title="Edit Header"
      >
        <Pencil className="h-4 w-4 text-slate-500" />
      </Button>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 xl:gap-x-4">
        {fields.map((f) => (
          <div key={f.label} className="min-w-0">
            <dt className="text-[11px] font-normal text-slate-900 mb-1">
              {f.label}
            </dt>
            <dd className="text-sm font-bold text-slate-900">{f.value}</dd>
          </div>
        ))}
      </dl>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Quote Header</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Quoted By (Salesperson)</label>
              <div className="border rounded-md">
                <EmployeePicker
                  value={ownerId}
                  valueLabel={ownerName}
                  onChange={(id, emp) => {
                    setOwnerId(id);
                    setOwnerName(emp?.fullName ?? null);
                  }}
                  placeholder="Search for an employee..."
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Most header fields are system-generated or linked to other records (like Customer Details).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => update.mutate()} disabled={update.isPending}>
              {update.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
